import React, { useState, useEffect } from 'react';
import { useAccount } from 'wagmi';
import axios from 'axios';
import { apiUrl } from '../utils/constants.ts';
import TopBar from '../components/TopBar';


const AdminPanel: React.FC = () => {
  const { address } = useAccount();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [moderators, setModerators] = useState<any[]>([]);
  const [kycSubmissions, setKycSubmissions] = useState<any[]>([]);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTokenQuery, setSearchTokenQuery] = useState('');
  const [newModeratorWallet, setNewModeratorWallet] = useState('');
  const [newModeratorUsername, setNewModeratorUsername] = useState('');
  const [newModeratorEmail, setNewModeratorEmail] = useState('');
  const [banAddress, setBanAddress] = useState('');
  const [banReason, setBanReason] = useState('');
  const [pending, setPending] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });
  // Check if FORCE_ADMIN is enabled in .env
  const forceAdmin = process.env.REACT_APP_FORCE_ADMIN === 'true';
  useEffect(() => {
    const checkAdmin = async () => {
      // If FORCE_ADMIN is true, bypass wallet check and set as admin
      if (forceAdmin) {
        setIsAdmin(true);
        fetchData();
        setLoading(false);
        return;
      }

      if (!address) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${apiUrl}/api/isAdmin?address=${address}`);
        setIsAdmin(response.data.isAdmin);
        if (response.data.isAdmin) {
          fetchData();
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdmin();
  }, [address, forceAdmin, apiUrl]);

  const fetchData = async () => {
    try {
      const [warningsRes, reportsRes, moderatorsRes, settingsRes, kycSubmissions] = await Promise.all([
        axios.get(`${apiUrl}/api/getWarnings`),
        axios.get(`${apiUrl}/api/getReports`),
        axios.get(`${apiUrl}/api/getModerators`),
        axios.get(`${apiUrl}/api/getSettings`),
        axios.get(`${apiUrl}/api/getKycSubmissions`),
      ]);
      setUsers(warningsRes.data);
      setReports(reportsRes.data);
      setModerators(moderatorsRes.data);
      setKycSubmissions(kycSubmissions.data);
      setEmailAlertsEnabled(settingsRes.data.emailAlertsEnabled);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleBan = async (ownerAddress: string, reason?: string) => {
    const banReasonInput = reason || prompt("Enter reason for banning:");
    if (!banReasonInput) return;
    try {
      setPending(true);
      await axios.post(`${apiUrl}/api/banUser`, {
        address: forceAdmin ? 'forced_admin' : address,
        ownerAddress,
        reason: banReasonInput,
      });
      fetchData();
      setTimeout(function () {
        setPending(false);
      }, 1500)
    } catch (error) {
      setPending(false);
      console.error("Error banning user:", error);
    }
  };

  const handleUnban = async (ownerAddress: string) => {
    try {
      setPending(true);
      await axios.post(`${apiUrl}/api/unbanUser`, {
        address: forceAdmin ? 'forced_admin' : address,
        ownerAddress,
      });
      fetchData();
      setTimeout(function () {
        setPending(false);
      }, 1500)
    } catch (error) {
      setPending(false);
      console.error("Error unbanning user:", error);
    }
  };

  const handleKycApprove = async (tokenAddress: string, approve?: string) => {
    try {
      setPending(true);
      await axios.post(`${apiUrl}/api/kycApprove`, {
        tokenAddress,
        approved: approve,
      });
      fetchData();
      setTimeout(function () {
        setPending(false);
      }, 1500)
    } catch (error) {
      setPending(false);
      console.error("Error approving kyc:", error);
    }
  };

  const handleUpdateReportStatus = async (timestamp: number, status: string) => {
    try {
      setPending(true);
      await axios.post(`${apiUrl}/api/updateReportStatus`, {
        address: forceAdmin ? 'forced_admin' : address,
        timestamp,
        status,
      });
      fetchData();
      setTimeout(function () {
        setPending(false);
      }, 1500)
    } catch (error) {
      setPending(false);
      console.error("Error updating report status:", error);
    }
  };

  const handleAddModerator = async () => {
    if (!newModeratorWallet) return;
    try {
      await axios.post(`${apiUrl}/api/addModerator`, {
        address: forceAdmin ? 'forced_admin' : address,
        walletAddress: newModeratorWallet,
        username: newModeratorUsername || undefined,
        email: newModeratorEmail || undefined,
      });
      setNewModeratorWallet('');
      setNewModeratorUsername('');
      setNewModeratorEmail('');
      fetchData();
    } catch (error) {
      console.error("Error adding moderator:", error);
    }
  };

  const handleRemoveModerator = async (walletAddress: string) => {
    try {
      await axios.post(`${apiUrl}/api/removeModerator`, {
        address: forceAdmin ? 'forced_admin' : address,
        walletAddress,
      });
      fetchData();
    } catch (error) {
      console.error("Error removing moderator:", error);
    }
  };

  const handleToggleEmailAlerts = async () => {
    try {
      await axios.post(`${apiUrl}/api/updateSettings`, {
        address: forceAdmin ? 'forced_admin' : address,
        emailAlertsEnabled: !emailAlertsEnabled,
      });
      setEmailAlertsEnabled(!emailAlertsEnabled);
    } catch (error) {
      console.error("Error toggling email alerts:", error);
    }
  };

  const handleBanByAddress = async () => {
    if (!banAddress || !banReason) return;
    await handleBan(banAddress, banReason);
    setBanAddress('');
    setBanReason('');
  };

  const filteredUsers = users.filter((user) =>
    user.ownerAddress.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredKyc = kycSubmissions.filter((kycSubmission) =>
    kycSubmission.tokenAddress.toLowerCase().includes(searchTokenQuery.toLowerCase()) || kycSubmission.email.toLowerCase().includes(searchTokenQuery.toLowerCase())
  );

  const copyAddress = (address: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (document.queryCommandSupported('copy')) {
      const ele = document.createElement('textarea');
      ele.value = address;
      document.body.appendChild(ele);
      ele.select();
      document.execCommand('copy');
      document.body.removeChild(ele);

      // Set tooltip position and text
      setTooltip({
        visible: true,
        x: e.pageX, // Use pageX for horizontal position
        y: e.pageY, // Use pageY for vertical position
        text: 'Copied!',
      });

      // Hide tooltip after 1 second
      setTimeout(() => {
        setTooltip({ visible: false, x: 0, y: 0, text: '' });
      }, 1500);
    }
  };

  const ItemsPerPage = 5

  const [currentReportsPage, setCurrentReportsPage] = useState(1)
  const [reportsTotalPages, setReportsTotalPages] = useState(0)
  const [reportPageNumbers, setReportsPageNumbers] = useState<(string | number)[]>([])
  const calculateReportsPageNumbers = (totalPages, currentPage) => {
    let pages: (number | string)[] = []
    if (totalPages <= 4) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    } else {
      if (currentPage === 1) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else if (currentPage === totalPages) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else {
        if (currentPage + 1 < totalPages) {
          if (currentPage + 1 === totalPages - 1) {
            pages = [currentPage - 1, currentPage, currentPage + 1, totalPages]
          } else {
            pages = [
              currentPage - 1,
              currentPage,
              currentPage + 1,
              '...',
              totalPages
            ]
          }
        } else if (currentPage < totalPages) {
          pages = [currentPage - 1, currentPage, currentPage + 1]
        } else {
          pages = [1, 2, '...', totalPages - 1, totalPages]
        }
      }
    }

    return pages
  }

  const handleReportsPageChange = newPageNumber => {
    setCurrentReportsPage(newPageNumber)
    setReportsPageNumbers(
      calculateReportsPageNumbers(reportsTotalPages, newPageNumber)
    )
  }
  useEffect(() => {
    const totalReportsPages = Math.ceil(
      reports.length / ItemsPerPage
    )
    setReportsTotalPages(totalReportsPages)
    setReportsPageNumbers(
      calculateReportsPageNumbers(totalReportsPages, currentReportsPage)
    )
  }, [reports])

  const [currentBanUsersPage, setCurrentBanUsersPage] = useState(1)
  const [banUsersTotalPages, setBanUsersTotalPages] = useState(0)
  const [banUsersPageNumbers, setBanUsersPageNumbers] = useState<(string | number)[]>([])
  const calculateBanUsersPageNumbers = (totalPages, currentPage) => {
    let pages: (number | string)[] = []
    if (totalPages <= 4) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    } else {
      if (currentPage === 1) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else if (currentPage === totalPages) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else {
        if (currentPage + 1 < totalPages) {
          if (currentPage + 1 === totalPages - 1) {
            pages = [currentPage - 1, currentPage, currentPage + 1, totalPages]
          } else {
            pages = [
              currentPage - 1,
              currentPage,
              currentPage + 1,
              '...',
              totalPages
            ]
          }
        } else if (currentPage < totalPages) {
          pages = [currentPage - 1, currentPage, currentPage + 1]
        } else {
          pages = [1, 2, '...', totalPages - 1, totalPages]
        }
      }
    }

    return pages
  }

  const handleBanUsersPageChange = newPageNumber => {
    setCurrentBanUsersPage(newPageNumber)
    setBanUsersPageNumbers(
      calculateBanUsersPageNumbers(banUsersTotalPages, newPageNumber)
    )
  }

  useEffect(() => {
    const totalUsersPages = Math.ceil(
      users.length / ItemsPerPage
    )
    setBanUsersTotalPages(totalUsersPages)
    setBanUsersPageNumbers(
      calculateBanUsersPageNumbers(totalUsersPages, currentBanUsersPage)
    )
  }, [users])

  const [currentKycPage, setCurrentKycPage] = useState(1)
  const [kycTotalPages, setKycTotalPages] = useState(0)
  const [kycPageNumbers, setKycPageNumbers] = useState<(string | number)[]>([])
  const calculateKycPageNumbers = (totalPages, currentPage) => {
    let pages: (number | string)[] = []
    if (totalPages <= 4) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    } else {
      if (currentPage === 1) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else if (currentPage === totalPages) {
        pages = [1, 2, '...', totalPages - 1, totalPages]
      } else {
        if (currentPage + 1 < totalPages) {
          if (currentPage + 1 === totalPages - 1) {
            pages = [currentPage - 1, currentPage, currentPage + 1, totalPages]
          } else {
            pages = [
              currentPage - 1,
              currentPage,
              currentPage + 1,
              '...',
              totalPages
            ]
          }
        } else if (currentPage < totalPages) {
          pages = [currentPage - 1, currentPage, currentPage + 1]
        } else {
          pages = [1, 2, '...', totalPages - 1, totalPages]
        }
      }
    }

    return pages
  }

  const handleKycPageChange = newPageNumber => {
    setCurrentKycPage(newPageNumber)
    setKycPageNumbers(
      calculateKycPageNumbers(kycTotalPages, newPageNumber)
    )
  }

  useEffect(() => {
    const totalUsersPages = Math.ceil(
      kycSubmissions.length / ItemsPerPage
    )
    setKycTotalPages(totalUsersPages)
    setKycPageNumbers(
      calculateKycPageNumbers(totalUsersPages, currentKycPage)
    )
  }, [kycSubmissions])

  return (
    <div className="GlobalContainer">
      {tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            top: tooltip.y + 15,
            left: tooltip.x + 15,
            backgroundColor: '#333',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: '5px',
            fontSize: '12px',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        >
          {tooltip.text}
        </div>
      )}

      <div style={{ zIndex: 1 }}>
        <TopBar showBanner={false} /> {/* Consistent navigation bar */}
        <div className="max-w-7xl mx-auto pt-36 pb-24 sm:py-10 px-4 sm:px-12">
          {loading ? (
            <div className="text-white text-center">Loading...</div>
          ) : (!address && !forceAdmin) ? (
            <div className="text-white text-center">
              <p>Please connect your wallet to access the Admin Panel.</p>
              <div className="mt-4 flex justify-center">
                <w3m-button /> {/* Centered wallet connect button */}
              </div>
            </div>
          ) : !isAdmin ? (
            <div className="text-white text-center">
              <h2>Access Denied</h2>
              <p>
                You do not have admin privileges. Only authorized admin wallet addresses can access this panel.
                Please contact support if you believe this is an error.
              </p>
            </div>
          ) : (
            <div className="text-white max-w-5xl mx-auto">
              <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>

              {/* Search Bar */}
              <input
                type="text"
                placeholder="Search by wallet address"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full mb-6 bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] rounded-[10px] p-4"
              />

              {/* Users Section */}
              <section className="mb-10">
                <h2 className="text-2xl font-semibold mb-4">Users</h2>
                <div className="bg-[#6d79ad33] rounded-[10px] backdrop-blur-[8px] border-2 lg:px-8 px-2.5 py-5 border-[#ffffff66]">
                  <div className="w-full text-xs sm:text-sm">
                    <div className="flex flex-wrap sm:grid sm:grid-cols-5 text-[#cfcfcf]">
                      <div className="uppercase text-center px-2 py-3">Wallet Address</div>
                      <div className="uppercase text-center px-2 py-3">Warnings</div>
                      <div className="uppercase text-center px-2 py-3">Banned</div>
                      <div className="uppercase text-center px-2 py-3">Reason</div>
                      <div className="uppercase text-center px-2 py-3">Actions</div>
                    </div>
                    <div className="gap-2 flex flex-col text-[#f8ffe8] overflow-y-auto no-scrollbar h-[268px]">
                      {filteredUsers.map((user) => (
                        <div key={user.ownerAddress} className="flex flex-wrap sm:grid sm:grid-cols-5 bg-[#010101] rounded-[10px] items-center">
                          <button className="px-2 sm:px-4 py-2.5 mx-auto" title={user.ownerAddress} onClick={copyAddress(user.ownerAddress)}>
                            {`${user.ownerAddress.slice(0, 6)}...${user.ownerAddress.slice(-4)}`}
                          </button>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{user.count || 0}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{user.banned ? 'Yes' : 'No'}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">
                            {user.banReason || (user.count >= 3 ? 'Automated Ban' : '')}
                          </div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">
                            <button
                              onClick={() => (user.banned ? handleUnban(user.ownerAddress) : handleBan(user.ownerAddress))}
                              className="p-2 rounded-[10px] text-white bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90"
                              disabled={pending ? true : false}
                            >
                              {user.banned ? 'Unban' : 'Ban'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    className="flex justify-center my-4 border border-gray-600 rounded-[10px] w-fit mx-auto"
                    style={{ textAlign: 'right' }}
                  >
                    {/* Render the "First Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() => handleBanUsersPageChange(1)}
                    >
                      &lt;&lt;
                    </button>
                    {/* Render the "Previous Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleBanUsersPageChange(
                          Math.max(currentBanUsersPage - 1, 1)
                        )
                      }
                    >
                      &lt;
                    </button>
                    {banUsersPageNumbers.map((pageNumber, index) => {
                      if (typeof pageNumber === 'number') {
                        return (
                          <button
                            key={pageNumber}
                            className={`px-2 py-1 mx-1 ${currentBanUsersPage === pageNumber
                              ? 'bg-[#01010100] text-white'
                              : 'bg-[#129a0a00] text-[#777]'
                              } rounded-lg`}
                            onClick={() =>
                              handleBanUsersPageChange(pageNumber)
                            }
                          >
                            {pageNumber}
                          </button>
                        )
                      } else {
                        return (
                          <span
                            key={pageNumber}
                            className="px-2 py-1 mx-1 bg-transparent text-white rounded-lg"
                          >
                            ...
                          </span>
                        )
                      }
                    })}
                    {/* Render the "Next Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleBanUsersPageChange(
                          Math.min(
                            currentReportsPage + 1,
                            reportsTotalPages
                          )
                        )
                      }
                    >
                      &gt;
                    </button>
                    {/* Render the "Last Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleBanUsersPageChange(reportsTotalPages)
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                </div>
              </section>

              {/* Ban User by Address Section */}
              <section className="mb-10">
                <h2 className="text-2xl font-semibold mb-4">Ban User by Address</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    value={banAddress}
                    onChange={(e) => setBanAddress(e.target.value)}
                    placeholder="Wallet Address"
                    className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] flex-grow"
                  />
                  <input
                    type="text"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Reason for ban"
                    className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] flex-grow"
                  />
                  <button
                    onClick={handleBanByAddress}
                    disabled={!banAddress || !banReason}
                    className={`p-2 rounded-[10px] text-white w-full sm:w-32 ${!banAddress || !banReason
                      ? 'bg-gray-500'
                      : 'bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90'
                      }`}
                  >
                    Ban User
                  </button>
                </div>
              </section>

              {/* Reports Section */}
              <section className="mb-10">
                <h2 className="text-2xl font-semibold mb-4">Reports</h2>
                <div className="bg-[#6d79ad33] rounded-[10px] backdrop-blur-[8px] border-2 lg:px-8 px-2.5 py-5 border-[#ffffff66]">
                  <div className="w-full text-xs sm:text-sm">
                    <div className="flex flex-wrap sm:grid sm:grid-cols-6 text-[#cfcfcf]">
                      <div className="uppercase text-center px-2 py-3">Reporter</div>
                      <div className="uppercase text-center px-2 py-3">Reported User</div>
                      <div className="uppercase text-center px-2 py-3">Reason</div>
                      <div className="uppercase text-center px-2 py-3">Timestamp</div>
                      <div className="uppercase text-center px-2 py-3">Status</div>
                      <div className="uppercase text-center px-2 py-3">Actions</div>
                    </div>
                    <div className="gap-2 flex flex-col text-[#f8ffe8] overflow-y-auto no-scrollbar h-[268px]">
                      {reports
                        .slice(
                          (currentReportsPage - 1) *
                          ItemsPerPage,
                          currentReportsPage * ItemsPerPage
                        )
                        .map((report) => (
                          <div key={report._id} className="flex flex-wrap sm:grid sm:grid-cols-6 bg-[#010101] rounded-[10px] items-center">
                            <button className="px-2 sm:px-4 py-2.5 mx-auto" title={report.reporter} onClick={copyAddress(report.reporter)}>
                              {`${report.reporter.slice(0, 6)}...${report.reporter.slice(-4)}`}
                            </button>
                            <button className="px-2 sm:px-4 py-2.5 mx-auto" title={report.reportedUser} onClick={copyAddress(report.reportedUser)}>
                              {`${report.reportedUser.slice(0, 6)}...${report.reportedUser.slice(-4)}`}
                            </button>
                            <div className="px-2 sm:px-4 py-2.5 mx-auto">{report.reason}</div>
                            <div className="px-2 sm:px-4 py-2.5 mx-auto">{new Date(report.timestamp).toLocaleString()}</div>
                            <div className="px-2 sm:px-4 py-2.5 mx-auto">{report.status}</div>
                            <div className="px-2 sm:px-4 py-2.5 mx-auto">
                              <select
                                value={report.status}
                                onChange={(e) => handleUpdateReportStatus(report.timestamp, e.target.value)}
                                className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-[#338282] border-[#131722]"
                                disabled={pending ? true : false}
                              >
                                <option value="pending">Pending</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="action_taken">Action Taken</option>
                              </select>
                            </div>
                          </div>
                        ))}
                    </div>
                    <div
                      className="flex justify-center my-4 border border-gray-600 rounded-[10px] w-fit mx-auto"
                      style={{ textAlign: 'right' }}
                    >
                      {/* Render the "First Page" button */}
                      <button
                        className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                        onClick={() => handleReportsPageChange(1)}
                      >
                        &lt;&lt;
                      </button>
                      {/* Render the "Previous Page" button */}
                      <button
                        className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                        onClick={() =>
                          handleReportsPageChange(
                            Math.max(currentReportsPage - 1, 1)
                          )
                        }
                      >
                        &lt;
                      </button>
                      {reportPageNumbers.map((pageNumber, index) => {
                        if (typeof pageNumber === 'number') {
                          return (
                            <button
                              key={pageNumber}
                              className={`px-2 py-1 mx-1 ${currentReportsPage === pageNumber
                                ? 'bg-[#01010100] text-white'
                                : 'bg-[#129a0a00] text-[#777]'
                                } rounded-lg`}
                              onClick={() =>
                                handleReportsPageChange(pageNumber)
                              }
                            >
                              {pageNumber}
                            </button>
                          )
                        } else {
                          return (
                            <span
                              key={pageNumber}
                              className="px-2 py-1 mx-1 bg-transparent text-white rounded-lg"
                            >
                              ...
                            </span>
                          )
                        }
                      })}
                      {/* Render the "Next Page" button */}
                      <button
                        className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                        onClick={() =>
                          handleReportsPageChange(
                            Math.min(
                              currentReportsPage + 1,
                              reportsTotalPages
                            )
                          )
                        }
                      >
                        &gt;
                      </button>
                      {/* Render the "Last Page" button */}
                      <button
                        className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                        onClick={() =>
                          handleReportsPageChange(reportsTotalPages)
                        }
                      >
                        &gt;&gt;
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              {/* Moderators Section */}
              <section className="mb-10">
                <h2 className="text-2xl font-semibold mb-4">Moderators</h2>
                <div className="bg-[#6d79ad33] rounded-[10px] backdrop-blur-[8px] border-2 lg:px-8 px-2.5 py-5 border-[#ffffff66]">
                  <div className="w-full text-xs sm:text-sm">
                    <div className="grid grid-cols-4 text-[#cfcfcf]">
                      <div className="uppercase text-center px-2 py-3">Wallet Address</div>
                      <div className="uppercase text-center px-2 py-3">Username</div>
                      <div className="uppercase text-center px-2 py-3">Email</div>
                      <div className="uppercase text-center px-2 py-3">Actions</div>
                    </div>
                    <div className="gap-2 flex flex-col text-[#f8ffe8] overflow-y-auto no-scrollbar h-[268px]">
                      {moderators.map((mod) => (
                        <div key={mod.walletAddress} className="flex flex-wrap sm:grid sm:grid-cols-4 bg-[#010101] rounded-[10px] items-center">
                          <div className="px-2 sm:px-4 py-2.5 mx-auto" title={mod.walletAddress}>
                            {`${mod.walletAddress.slice(0, 6)}...${mod.walletAddress.slice(-4)}`}
                          </div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{mod.username || '-'}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{mod.email || '-'}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">
                            <button
                              onClick={() => handleRemoveModerator(mod.walletAddress)}
                              className="p-2 rounded-[10px] text-white bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <input
                    type="text"
                    value={newModeratorWallet}
                    onChange={(e) => setNewModeratorWallet(e.target.value)}
                    placeholder="Wallet Address"
                    className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] flex-grow"
                  />
                  <input
                    type="text"
                    value={newModeratorUsername}
                    onChange={(e) => setNewModeratorUsername(e.target.value)}
                    placeholder="Username (optional)"
                    className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] flex-grow"
                  />
                  <input
                    type="email"
                    value={newModeratorEmail}
                    onChange={(e) => setNewModeratorEmail(e.target.value)}
                    placeholder="Email (optional)"
                    className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] flex-grow"
                  />
                  <button
                    onClick={handleAddModerator}
                    disabled={!newModeratorWallet}
                    className={`p-2 rounded-[10px] text-white w-full sm:w-32 ${
                      !newModeratorWallet
                        ? 'bg-gray-500'
                        : 'bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90'
                    }`}
                  >
                    Add Moderator
                  </button>
                </div>
              </section>

              {/* kyc approval section */}

              {/* Search Bar */}
              <input
                type="text"
                placeholder="Search by token address"
                value={searchTokenQuery}
                onChange={(e) => setSearchTokenQuery(e.target.value)}
                className="w-full mb-6 bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-white border-[#131722] rounded-[10px] p-4"
              />
              <section className="mb-10">
                <h2 className="text-2xl font-semibold mb-4">KYC Lists</h2>
                <div className="bg-[#6d79ad33] rounded-[10px] backdrop-blur-[8px] border-2 lg:px-8 px-2.5 py-5 border-[#ffffff66]">
                  <div className="w-full text-xs sm:text-sm">
                    <div className="flex flex-wrap sm:grid sm:grid-cols-5 text-[#cfcfcf]">
                      <div className="uppercase text-center px-2 py-3">Token Address</div>
                      <div className="uppercase text-center px-2 py-3">Email</div>
                      <div className="uppercase text-center px-2 py-3">Full Name</div>
                      <div className="uppercase text-center px-2 py-3">Project Name</div>
                      <div className="uppercase text-center px-2 py-3">Status</div>
                    </div>
                    <div className="gap-2 flex flex-col text-[#f8ffe8] overflow-y-auto no-scrollbar h-[268px]">
                      {filteredKyc.map((kycSubmission) => (
                        <div key={kycSubmission.tokenAddress} className="flex flex-wrap sm:grid sm:grid-cols-5 bg-[#010101] rounded-[10px] items-center">
                          <button className="px-2 sm:px-4 py-2.5 mx-auto" title={kycSubmission.tokenAddress} onClick={copyAddress(kycSubmission.tokenAddress)}>
                            {`${kycSubmission.tokenAddress.slice(0, 6)}...${kycSubmission.tokenAddress.slice(-4)}`}
                          </button>
                          <button className="px-2 sm:px-4 py-2.5 mx-auto" title={kycSubmission.email} onClick={copyAddress(kycSubmission.email)}>
                            {`${kycSubmission.email.slice(0, 6)}...${kycSubmission.email.slice(-4)}`}
                          </button>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{kycSubmission.fullName}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">{kycSubmission.projectName}</div>
                          <div className="px-2 sm:px-4 py-2.5 mx-auto">
                            <select
                              value={kycSubmission.status}
                              onChange={(e) => handleKycApprove(kycSubmission.tokenAddress, e.target.value)}
                              className="p-2 rounded-[10px] bg-[#6d79ad33] backdrop-blur-[8px] border-2 text-[#338282] border-[#131722]"
                              disabled={pending ? true : false}
                            >
                              <option value="pending">Pending</option>
                              <option value="approve">Approve</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div
                    className="flex justify-center my-4 border border-gray-600 rounded-[10px] w-fit mx-auto"
                    style={{ textAlign: 'right' }}
                  >
                    {/* Render the "First Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() => handleKycPageChange(1)}
                    >
                      &lt;&lt;
                    </button>
                    {/* Render the "Previous Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleKycPageChange(
                          Math.max(currentKycPage - 1, 1)
                        )
                      }
                    >
                      &lt;
                    </button>
                    {kycPageNumbers.map((pageNumber, index) => {
                      if (typeof pageNumber === 'number') {
                        return (
                          <button
                            key={pageNumber}
                            className={`px-2 py-1 mx-1 ${currentKycPage === pageNumber
                              ? 'bg-[#01010100] text-white'
                              : 'bg-[#129a0a00] text-[#777]'
                              } rounded-lg`}
                            onClick={() =>
                              handleKycPageChange(pageNumber)
                            }
                          >
                            {pageNumber}
                          </button>
                        )
                      } else {
                        return (
                          <span
                            key={pageNumber}
                            className="px-2 py-1 mx-1 bg-transparent text-white rounded-lg"
                          >
                            ...
                          </span>
                        )
                      }
                    })}
                    {/* Render the "Next Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleKycPageChange(
                          Math.min(
                            currentKycPage + 1,
                            kycTotalPages
                          )
                        )
                      }
                    >
                      &gt;
                    </button>
                    {/* Render the "Last Page" button */}
                    <button
                      className="px-2 py-1 mx-1 bg-primary text-white rounded-lg"
                      onClick={() =>
                        handleKycPageChange(kycTotalPages)
                      }
                    >
                      &gt;&gt;
                    </button>
                  </div>
                </div>
              </section>

              {/* Settings Section */}
              <section>
                <h2 className="text-2xl font-semibold mb-4">Settings</h2>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={emailAlertsEnabled}
                    onChange={handleToggleEmailAlerts}
                    className="w-5 h-5"
                  />
                  Email Alerts
                </label>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;